import React, { useState } from 'react';
import { FileTextIcon, TrendingUpIcon, AlertCircleIcon, ClipboardCheckIcon, CheckCircleIcon, ClockIcon, CalendarIcon, TruckIcon, BarChartIcon, PlusIcon, FilterIcon, ShoppingCartIcon, XIcon, CheckIcon, TagIcon, DollarSignIcon, PackageIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function ProcurementDashboard({
  requisitions,
  purchaseOrders,
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalRequisitions = requisitions.length;
  const pendingApproval = requisitions.filter(req => req.status === 'Pending Approval').length;
  const totalReqSpend = requisitions.reduce((sum, req) => sum + req.total, 0);
  // PO statistics
  const totalPurchaseOrders = purchaseOrders.length;
  const pendingPOs = purchaseOrders.filter(po => po.status === 'Pending Confirmation' || po.status === 'Draft').length;
  const totalPOSpend = purchaseOrders.reduce((sum, po) => sum + po.total, 0);
  // Get recent requisitions (last 3)
  const recentRequisitions = [...requisitions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Get recent POs (last 3)
  const recentPurchaseOrders = [...purchaseOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 3);
  // Sample data for procurement insights
  const pendingTasks = [{
    id: 'TASK-001',
    type: 'Requisition',
    name: 'Office Supplies Approval',
    dueDate: '2023-11-20',
    priority: 'High',
    status: 'Pending Approval'
  }, {
    id: 'TASK-002',
    type: 'Purchase Order',
    name: 'Server Hardware Confirmation',
    dueDate: '2023-11-18',
    priority: 'Medium',
    status: 'Pending Confirmation'
  }, {
    id: 'TASK-003',
    type: 'Receipt',
    name: 'IT Equipment Delivery',
    dueDate: '2023-11-25',
    priority: 'Low',
    status: 'Expected'
  }];
  const upcomingDeliveries = [{
    id: 'PO-2023-001',
    vendor: 'Server Solutions Inc.',
    items: 'Server Hardware',
    expectedDate: '2023-11-22',
    status: 'In Transit',
    trackingNumber: 'TRK123456789'
  }, {
    id: 'PO-2023-002',
    vendor: 'PrintWorks Inc.',
    items: 'Marketing Materials',
    expectedDate: '2023-11-25',
    status: 'Processing',
    trackingNumber: null
  }];
  const topSpendCategories = [{
    category: 'IT Equipment',
    spend: 12500,
    percentage: 40
  }, {
    category: 'Office Supplies',
    spend: 8000,
    percentage: 25
  }, {
    category: 'Marketing',
    spend: 6200,
    percentage: 20
  }, {
    category: 'Services',
    spend: 4800,
    percentage: 15
  }];
  // Dashboard time filter state
  const [timeFilter, setTimeFilter] = useState('30days');
  return <div data-id="element-14070">
      <div className="mb-8 flex justify-between items-center" data-id="element-14071">
        <div data-id="element-14072">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-14073">
            Procurement Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-14074">
            Overview of requisitions, purchase orders, and goods receipts
          </p>
        </div>
        <div className="flex items-center space-x-3" data-id="element-14075">
          <div className="flex items-center" data-id="element-14076">
            <FilterIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-14077" />
            <select value={timeFilter} onChange={e => setTimeFilter(e.target.value)} className="block text-sm border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" data-id="element-14078">
              <option value="7days" data-id="element-14079">Last 7 days</option>
              <option value="30days" data-id="element-14080">Last 30 days</option>
              <option value="90days" data-id="element-14081">Last 90 days</option>
              <option value="ytd" data-id="element-14082">Year to date</option>
            </select>
          </div>
          <button onClick={() => setCurrentView('create-requisition')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14083">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-14084" />
            New Requisition
          </button>
        </div>
      </div>
      {/* Summary Cards Section */}
      <div className="mb-8" data-id="element-14085">
        <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-14086">
          Procurement Overview
        </h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-14087">
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-14088">
            <div className="p-5" data-id="element-14089">
              <div className="flex items-center" data-id="element-14090">
                <div className="flex-shrink-0" data-id="element-14091">
                  <FileTextIcon className="h-6 w-6 text-indigo-400" data-id="element-14092" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-14093">
                  <dl data-id="element-14094">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14095">
                      Active Requisitions
                    </dt>
                    <dd data-id="element-14096">
                      <div className="text-lg font-medium text-gray-900" data-id="element-14097">
                        {totalRequisitions}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('purchase-orders')} data-id="element-14098">
            <div className="p-5" data-id="element-14099">
              <div className="flex items-center" data-id="element-14100">
                <div className="flex-shrink-0" data-id="element-14101">
                  <ClipboardCheckIcon className="h-6 w-6 text-blue-400" data-id="element-14102" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-14103">
                  <dl data-id="element-14104">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14105">
                      Active Purchase Orders
                    </dt>
                    <dd data-id="element-14106">
                      <div className="text-lg font-medium text-gray-900" data-id="element-14107">
                        {totalPurchaseOrders}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" onClick={() => setCurrentView('requisitions')} data-id="element-14108">
            <div className="p-5" data-id="element-14109">
              <div className="flex items-center" data-id="element-14110">
                <div className="flex-shrink-0" data-id="element-14111">
                  <AlertCircleIcon className="h-6 w-6 text-yellow-400" data-id="element-14112" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-14113">
                  <dl data-id="element-14114">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14115">
                      Pending Approvals
                    </dt>
                    <dd data-id="element-14116">
                      <div className="text-lg font-medium text-gray-900" data-id="element-14117">
                        {pendingApproval + pendingPOs}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white overflow-hidden shadow rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200" data-id="element-14118">
            <div className="p-5" data-id="element-14119">
              <div className="flex items-center" data-id="element-14120">
                <div className="flex-shrink-0" data-id="element-14121">
                  <DollarSignIcon className="h-6 w-6 text-green-400" data-id="element-14122" />
                </div>
                <div className="ml-5 w-0 flex-1" data-id="element-14123">
                  <dl data-id="element-14124">
                    <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-14125">
                      Total Procurement Spend
                    </dt>
                    <dd data-id="element-14126">
                      <div className="text-lg font-medium text-gray-900" data-id="element-14127">
                        {formatCurrency(totalPOSpend, 'USD', locale)}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Procurement Metrics */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-14128">
        {/* Pending Tasks */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14129">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14130">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14131">
              Pending Tasks
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-14132">
              {pendingTasks.length} items
            </span>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14133">
            {pendingTasks.map(task => <li key={task.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => {
            if (task.type === 'Requisition') {
              setCurrentView('requisitions');
            } else if (task.type === 'Purchase Order') {
              setCurrentView('purchase-orders');
            } else {
              setCurrentView('receipting');
            }
          }} data-id="element-14134">
                <div className="flex items-center justify-between" data-id="element-14135">
                  <div className="flex items-center" data-id="element-14136">
                    <div className="flex-shrink-0" data-id="element-14137">
                      {task.type === 'Requisition' ? <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-14138" /> : task.type === 'Purchase Order' ? <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-14139" /> : <TruckIcon className="h-5 w-5 text-green-400" data-id="element-14140" />}
                    </div>
                    <div className="ml-3" data-id="element-14141">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14142">
                        {task.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14143">
                        {task.id} • {task.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14144">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${task.priority === 'High' ? 'bg-red-100 text-red-800' : task.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-14145">
                      {task.priority}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14146">
                      Due: {task.dueDate}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        {/* Upcoming Deliveries */}
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14147">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14148">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14149">
              Upcoming Deliveries
            </h3>
            <button onClick={() => setCurrentView('receipting')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-14150">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14151">
            {upcomingDeliveries.map(delivery => <li key={delivery.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('receipting')} data-id="element-14152">
                <div className="flex items-center justify-between" data-id="element-14153">
                  <div className="flex items-center" data-id="element-14154">
                    <div className="flex-shrink-0" data-id="element-14155">
                      <TruckIcon className="h-5 w-5 text-blue-400" data-id="element-14156" />
                    </div>
                    <div className="ml-3" data-id="element-14157">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14158">
                        {delivery.items}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14159">
                        {delivery.id} • {delivery.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14160">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${delivery.status === 'In Transit' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14161">
                      {delivery.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14162">
                      Expected: {delivery.expectedDate}
                    </span>
                  </div>
                </div>
                {delivery.trackingNumber && <div className="mt-2 text-xs text-gray-500" data-id="element-14163">
                    Tracking: {delivery.trackingNumber}
                  </div>}
              </li>)}
          </ul>
        </div>
      </div>
      {/* Spend by Category */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-14164">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-14165">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14166">
            Spend by Category
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-14167">
          <div className="space-y-4" data-id="element-14168">
            {topSpendCategories.map(category => <div key={category.category} data-id="element-14169">
                <div className="flex items-center justify-between mb-1" data-id="element-14170">
                  <div className="flex items-center" data-id="element-14171">
                    <TagIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-14172" />
                    <span className="text-sm font-medium text-gray-700" data-id="element-14173">
                      {category.category}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-14174">
                    {formatCurrency(category.spend, 'USD', locale)} (
                    {category.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-14175">
                  <div className="bg-indigo-600 h-2.5 rounded-full" style={{
                width: `${category.percentage}%`
              }} data-id="element-14176"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
      {/* Recent Requisitions and Purchase Orders */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 mb-8" data-id="element-14177">
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14178">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14179">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14180">
              Recent Requisitions
            </h3>
            <button onClick={() => setCurrentView('requisitions')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-14181">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14182">
            {recentRequisitions.map(req => <li key={req.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('requisitions')} data-id="element-14183">
                <div className="flex items-center justify-between" data-id="element-14184">
                  <div className="flex items-center" data-id="element-14185">
                    <div className="flex-shrink-0" data-id="element-14186">
                      <FileTextIcon className="h-5 w-5 text-indigo-400" data-id="element-14187" />
                    </div>
                    <div className="ml-3" data-id="element-14188">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14189">
                        {req.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14190">
                        {req.id} • {req.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14191">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${req.status === 'Approved' ? 'bg-green-100 text-green-800' : req.status === 'Draft' ? 'bg-gray-100 text-gray-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14192">
                      {req.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14193">
                      {formatCurrency(req.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14194">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-14195">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14196">
              Recent Purchase Orders
            </h3>
            <button onClick={() => setCurrentView('purchase-orders')} className="text-sm text-indigo-600 hover:text-indigo-900" data-id="element-14197">
              View all
            </button>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-14198">
            {recentPurchaseOrders.map(po => <li key={po.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50 cursor-pointer" onClick={() => setCurrentView('purchase-orders')} data-id="element-14199">
                <div className="flex items-center justify-between" data-id="element-14200">
                  <div className="flex items-center" data-id="element-14201">
                    <div className="flex-shrink-0" data-id="element-14202">
                      <ClipboardCheckIcon className="h-5 w-5 text-blue-400" data-id="element-14203" />
                    </div>
                    <div className="ml-3" data-id="element-14204">
                      <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-14205">
                        {po.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-14206">
                        {po.id} • {po.vendor}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center" data-id="element-14207">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${po.status === 'Sent to Vendor' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14208">
                      {po.status}
                    </span>
                    <span className="text-sm text-gray-500 ml-4" data-id="element-14209">
                      {formatCurrency(po.total, 'USD', locale)}
                    </span>
                  </div>
                </div>
              </li>)}
          </ul>
        </div>
      </div>
      {/* Procurement KPIs */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-8" data-id="element-14210">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-14211">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-14212">
            Procurement KPIs
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6" data-id="element-14213">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-14214">
            <div className="bg-green-50 p-4 rounded-lg" data-id="element-14215">
              <div className="flex items-center" data-id="element-14216">
                <div className="flex-shrink-0 bg-green-100 rounded-md p-2" data-id="element-14217">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" data-id="element-14218" />
                </div>
                <div className="ml-4" data-id="element-14219">
                  <h4 className="text-sm font-medium text-green-900" data-id="element-14220">
                    On-Time Delivery
                  </h4>
                  <p className="text-3xl font-semibold text-green-700" data-id="element-14221">92%</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg" data-id="element-14222">
              <div className="flex items-center" data-id="element-14223">
                <div className="flex-shrink-0 bg-blue-100 rounded-md p-2" data-id="element-14224">
                  <ClockIcon className="h-6 w-6 text-blue-600" data-id="element-14225" />
                </div>
                <div className="ml-4" data-id="element-14226">
                  <h4 className="text-sm font-medium text-blue-900" data-id="element-14227">
                    Avg. Processing Time
                  </h4>
                  <p className="text-3xl font-semibold text-blue-700" data-id="element-14228">
                    3.2 days
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg" data-id="element-14229">
              <div className="flex items-center" data-id="element-14230">
                <div className="flex-shrink-0 bg-indigo-100 rounded-md p-2" data-id="element-14231">
                  <PackageIcon className="h-6 w-6 text-indigo-600" data-id="element-14232" />
                </div>
                <div className="ml-4" data-id="element-14233">
                  <h4 className="text-sm font-medium text-indigo-900" data-id="element-14234">
                    Order Accuracy
                  </h4>
                  <p className="text-3xl font-semibold text-indigo-700" data-id="element-14235">98%</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}